import { type Terminal, X } from 'lucide-react';
import { type PointerEvent as ReactPointerEvent, useState } from 'react';
import { ItemIcon } from '@/components/item-icon';

/** Props injected onto a draggable tab element by the panel's drag controller. */
export type TabDragProps = {
  'data-panel-tab-id': string;
  onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
};

/**
 * A tab of the side-panel header: the tab itself (`role="tab"`, in its bar's tablist) plus a
 * close button revealed on hover (always shown on a touch screen), wired to the drag controller
 * through {@link TabDragProps}. The keyboard reaches one tab of the bar (the active one) and
 * moves with the arrows; Delete closes.
 */
export function TabButton({
  active,
  shown = false,
  dragProps,
  icon: Icon,
  iconUrl,
  iconSrc,
  name,
  badge,
  onClose,
  onSelect,
  focusable = active,
}: {
  active: boolean;
  /** The bar's one tab in the Tab order (the active one, else the first). */
  focusable?: boolean;
  /** Shown with the active tab, in the panel's split. */
  shown?: boolean;
  dragProps: TabDragProps;
  icon: typeof Terminal;
  /** An image (a page favicon) shown instead of `icon` until it fails to load. */
  iconUrl?: string;
  /** The icon of the app the tab shows (`display.icon`), painted like `icon`. */
  iconSrc?: string | undefined;
  name: string;
  /** A count beside the name (the files Review shows). */
  badge?: number | undefined;
  /** Absent: the tab stays (a project's Tasks, Context and Project). */
  onClose?: (() => void) | undefined;
  onSelect: () => void;
}) {
  const [failedIconUrl, setFailedIconUrl] = useState<string | null>(null);
  const showImage = iconUrl !== undefined && iconUrl !== failedIconUrl;
  return (
    <div
      {...dragProps}
      role="presentation"
      data-panel-tab-active={active}
      // The keyboard's focus ring (styles.css) rings the whole pill, close button included.
      className={`type-meta group flex h-8 min-w-0 max-w-44 shrink-0 items-center gap-1 rounded-control pr-1.5 pl-3 transition-colors has-[[data-panel-tab]:focus-visible]:shadow-[inset_0_0_0_calc(var(--focus-outline-width)+var(--focus-band-width))_var(--background)] has-[[data-panel-tab]:focus-visible]:outline-(length:--focus-outline-width) has-[[data-panel-tab]:focus-visible]:outline-ring has-[[data-panel-tab]:focus-visible]:outline-solid has-[[data-panel-tab]:focus-visible]:-outline-offset-2 ${
        active
          ? 'bg-control font-semibold text-foreground'
          : shown
            ? 'bg-control/50 text-foreground'
            : 'text-muted-foreground hover:bg-control/60 hover:text-foreground'
      }`}
    >
      <button
        aria-keyshortcuts={onClose === undefined ? undefined : 'Delete'}
        aria-selected={active}
        className="flex min-w-0 flex-1 items-center gap-1.5 rounded-control text-left focus-visible:!shadow-none focus-visible:!outline-none"
        data-panel-tab
        onClick={onSelect}
        onKeyDown={(event) => {
          if (onClose !== undefined && (event.key === 'Delete' || event.key === 'Backspace')) {
            event.preventDefault();
            onClose();
          }
        }}
        role="tab"
        tabIndex={focusable ? 0 : -1}
        type="button"
      >
        {showImage ? (
          <img
            alt=""
            className="size-3.5 shrink-0 rounded-sm object-contain"
            draggable={false}
            onError={() => setFailedIconUrl(iconUrl)}
            referrerPolicy="no-referrer"
            src={iconUrl}
          />
        ) : (
          <ItemIcon className="size-3.5" icon={Icon} src={iconSrc} />
        )}
        <span className="truncate">{name}</span>
        {badge !== undefined && badge > 0 && (
          <span className="shrink-0 font-normal text-muted-foreground tabular-nums">{badge}</span>
        )}
      </button>
      {onClose !== undefined && (
        <button
          // For the pointer only: a tablist holds tabs, and Delete on the tab closes it.
          aria-hidden
          aria-label={`Close ${name} tab`}
          className="flex size-5 shrink-0 items-center justify-center rounded-control text-muted-foreground can-hover:opacity-0 hover:bg-background/60 hover:text-foreground group-hover:opacity-80 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring"
          data-tab-close
          onClick={onClose}
          tabIndex={-1}
          type="button"
        >
          <X className="size-3" />
        </button>
      )}
    </div>
  );
}
